import {
  doc,
  getDoc,
  setDoc,
  deleteField,
  onSnapshot,
  updateDoc,
  Unsubscribe,
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
  type DocumentSnapshot,
  type DocumentReference,
} from 'firebase/firestore';
import { firestore } from '../utils/firebaseSetUp';
import { isSelfHosted } from '../config/infra';
import { resolveInventoryMode } from '../utils/inventoryMode';
import { authenticatedHttpClient } from './httpClient';
import {
  ConfigurationFactors,
  NotificationFactors,
  BrandingFactors,
  ProfitPlus2k8OrgConfig,
  ProfitPlus2k8ReferenceTables,
  CONFIGURATION_DEFAULTS,
  DEFAULT_NOTIFICATION_CONFIGURATION,
  DEFAULT_BRANDING_CONFIGURATION,
  DEFAULT_DELIVERY_NOTE_LAYOUT,
  DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG,
} from '../models/configuration';

// Use the default configurations from the models file
const DEFAULT_CONFIGURATION = CONFIGURATION_DEFAULTS;
const DEFAULT_NOTIFICATION_CONFIG = DEFAULT_NOTIFICATION_CONFIGURATION;
const DEFAULT_BRANDING_CONFIG = DEFAULT_BRANDING_CONFIGURATION;
const DEFAULT_TAXES = CONFIGURATION_DEFAULTS.taxes ?? [];

type FactorDocId = 'settings' | 'branding' | 'notifications';

async function fetchSelfHostedFactor(
  organizationId: string,
  factorId: FactorDocId
): Promise<Record<string, unknown> | null> {
  try {
    return await authenticatedHttpClient.get<Record<string, unknown>>(
      `/configuration/organizations/${organizationId}/factors/${factorId}`
    );
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw error;
  }
}

async function saveSelfHostedFactor(
  organizationId: string,
  factorId: FactorDocId,
  data: Record<string, unknown>
): Promise<void> {
  await authenticatedHttpClient.put(
    `/configuration/organizations/${organizationId}/factors/${factorId}`,
    data
  );
}

async function fetchSelfHostedProfitPlusIntegration(
  organizationId: string
): Promise<Record<string, unknown> | null> {
  try {
    return await authenticatedHttpClient.get<Record<string, unknown>>(
      `/configuration/organizations/${organizationId}/integrations/profit-plus-2k8`
    );
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw error;
  }
}

async function saveSelfHostedProfitPlusIntegration(
  organizationId: string,
  data: Record<string, unknown>
): Promise<void> {
  await authenticatedHttpClient.put(
    `/configuration/organizations/${organizationId}/integrations/profit-plus-2k8`,
    data
  );
}

const slugifyTaxId = (name: string): string => {
  const normalized = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || 'tax';
};

const getNextUniqueTaxId = (base: string, usedIds: Set<string>): string => {
  if (!usedIds.has(base)) return base;
  let counter = 2;
  while (usedIds.has(`${base}-${counter}`)) {
    counter += 1;
  }
  return `${base}-${counter}`;
};

/**
 * True when the org has persisted the minimum Profit reference export settings (wizard step 1).
 * Same rules as ProfitPlus2k8SetupModal «Guardar en configuración».
 */
export function hasPersistedProfitReferenceExportConfig(
  pp: Partial<ProfitPlus2k8OrgConfig> | null | undefined
): boolean {
  const alma = (pp?.orderExportAlmacen ?? '').trim();
  const mon = (pp?.orderExportDefaultMoneda ?? '').trim();
  const tasa = pp?.orderExportDefaultTasa;
  return (
    Boolean(alma) &&
    Boolean(mon) &&
    tasa != null &&
    Number.isFinite(Number(tasa)) &&
    Number(tasa) > 0
  );
}

/** Raw Firestore doc for integrations/profitPlus2k8 (integration fields are top-level). */
function profitReferenceExportSavedFromRawDoc(
  data: Record<string, unknown> | undefined
): boolean {
  if (!data) return false;
  return hasPersistedProfitReferenceExportConfig({
    orderExportAlmacen:
      typeof data.orderExportAlmacen === 'string' ? data.orderExportAlmacen : undefined,
    orderExportDefaultMoneda:
      typeof data.orderExportDefaultMoneda === 'string'
        ? data.orderExportDefaultMoneda
        : undefined,
    orderExportDefaultTasa:
      typeof data.orderExportDefaultTasa === 'number'
        ? data.orderExportDefaultTasa
        : typeof data.orderExportDefaultTasa === 'string' &&
            String(data.orderExportDefaultTasa).trim() !== ''
          ? Number(data.orderExportDefaultTasa)
          : undefined,
  });
}

/**
 * Effective initial-setup completion: explicit `false` wins (wizard reset);
 * else `true` if the flag is set or legacy reference-export fields exist on the doc.
 */
export function resolveProfitPlus2k8InitialSetupCompleted(
  data: Record<string, unknown> | undefined
): boolean {
  if (!data) return false;
  const derivedFromSavedFields = profitReferenceExportSavedFromRawDoc(data);
  const raw = data.initialSetupCompleted;
  if (raw === false || raw === 'false') return false;
  if (raw === true || raw === 'true') return true;
  return derivedFromSavedFields;
}

/**
 * When Profit Plus is enabled, ERP/sync UI in Inventory, Clients, Vendors, and Orders requires
 * completed initial setup (paso 1). When integration is off, returns true (no gating).
 */
export function isProfitPlus2k8ReadyForAppFeatures(
  pp: Partial<ProfitPlus2k8OrgConfig> | null | undefined
): boolean {
  if (!pp?.enabled) return true;
  return resolveProfitPlus2k8InitialSetupCompleted(pp as unknown as Record<string, unknown>);
}

/** Normalize Profit Plus 2k8 config from Firestore (e.g. Timestamp → Date). */
const normalizeProfitPlus2k8Config = (
  raw: unknown
): ProfitPlus2k8OrgConfig => {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG };
  const o = raw as Record<string, unknown>;
  const lastSyncAt = o.lastSyncAt;
  let lastSyncAtDate: Date | null | undefined;
  if (lastSyncAt == null) {
    lastSyncAtDate = null;
  } else if (lastSyncAt instanceof Date) {
    lastSyncAtDate = lastSyncAt;
  } else if (typeof (lastSyncAt as { toDate?: () => Date }).toDate === 'function') {
    lastSyncAtDate = (lastSyncAt as { toDate: () => Date }).toDate();
  } else if (typeof lastSyncAt === 'string' || typeof lastSyncAt === 'number') {
    lastSyncAtDate = new Date(lastSyncAt);
  } else {
    lastSyncAtDate = null;
  }
  const syncType = typeof o.syncType === 'string' && ['none', 'import_only', 'export_only', 'bidirectional'].includes(o.syncType)
    ? o.syncType as ProfitPlus2k8OrgConfig['syncType']
    : DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG.syncType;
  const syncEntities = Array.isArray(o.syncEntities) ? o.syncEntities as ProfitPlus2k8OrgConfig['syncEntities'] : DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG.syncEntities;
  const syncAcceptedSalesToProfit = typeof o.syncAcceptedSalesToProfit === 'boolean' ? o.syncAcceptedSalesToProfit : (DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG.syncAcceptedSalesToProfit ?? false);
  const askBeforeCreatingProfitPedido =
    typeof o.askBeforeCreatingProfitPedido === 'boolean'
      ? o.askBeforeCreatingProfitPedido
      : (DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG.askBeforeCreatingProfitPedido ?? true);
  const stockManagedInMayorX =
    typeof o.stockManagedInMayorX === 'boolean'
      ? o.stockManagedInMayorX
      : (DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG.stockManagedInMayorX ?? true);
  const saveProductChangesToProfit =
    typeof o.saveProductChangesToProfit === 'boolean'
      ? o.saveProductChangesToProfit
      : (DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG.saveProductChangesToProfit ?? true);
  const saveClientChangesToProfit =
    typeof o.saveClientChangesToProfit === 'boolean'
      ? o.saveClientChangesToProfit
      : (DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG.saveClientChangesToProfit ?? true);
  const saveSalesPersonChangesToProfit =
    typeof o.saveSalesPersonChangesToProfit === 'boolean'
      ? o.saveSalesPersonChangesToProfit
      : (DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG.saveSalesPersonChangesToProfit ?? true);
  const orderExportAlmacen =
    typeof o.orderExportAlmacen === 'string' && o.orderExportAlmacen.trim()
      ? o.orderExportAlmacen.trim()
      : undefined;
  const defaultProductStockSubAlmacen =
    typeof o.defaultProductStockSubAlmacen === 'string' && o.defaultProductStockSubAlmacen.trim()
      ? o.defaultProductStockSubAlmacen.trim()
      : undefined;
  const orderExportCurrencyMapping = o.orderExportCurrencyMapping && typeof o.orderExportCurrencyMapping === 'object' && !Array.isArray(o.orderExportCurrencyMapping)
    ? (o.orderExportCurrencyMapping as Record<string, string>)
    : (DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG.orderExportCurrencyMapping ?? {});
  const orderExportDefaultMoneda =
    typeof o.orderExportDefaultMoneda === 'string' && o.orderExportDefaultMoneda.trim()
      ? o.orderExportDefaultMoneda.trim()
      : undefined;
  const orderExportDefaultTasa =
    typeof o.orderExportDefaultTasa === 'number' && Number.isFinite(o.orderExportDefaultTasa)
      ? o.orderExportDefaultTasa
      : undefined;
  const orderExportDefaultIva =
    typeof o.orderExportDefaultIva === 'number' && Number.isFinite(o.orderExportDefaultIva)
      ? o.orderExportDefaultIva
      : undefined;
  const referenceTables = o.referenceTables && typeof o.referenceTables === 'object' ? (o.referenceTables as ProfitPlus2k8OrgConfig['referenceTables']) : undefined;
  const taxMapping = o.taxMapping && typeof o.taxMapping === 'object' && !Array.isArray(o.taxMapping) ? (o.taxMapping as Record<string, string>) : undefined;
  const syncEmployeeIFromLegacy =
    typeof o.syncEmployeeI === 'string' && o.syncEmployeeI.trim()
      ? o.syncEmployeeI.trim()
      : typeof o.syncEmployeeIClients === 'string' && o.syncEmployeeIClients.trim()
        ? o.syncEmployeeIClients.trim()
        : typeof o.syncEmployeeIProducts === 'string' && o.syncEmployeeIProducts.trim()
          ? o.syncEmployeeIProducts.trim()
          : typeof o.syncEmployeeISales === 'string' && o.syncEmployeeISales.trim()
            ? o.syncEmployeeISales.trim()
            : undefined;
  const initialSetupCompleted = resolveProfitPlus2k8InitialSetupCompleted(o);

  return {
    enabled: Boolean(o.enabled),
    syncType,
    syncAcceptedSalesToProfit,
    askBeforeCreatingProfitPedido,
    stockManagedInMayorX,
    saveProductChangesToProfit,
    saveClientChangesToProfit,
    saveSalesPersonChangesToProfit,
    initialSetupCompleted,
    ...(orderExportAlmacen ? { orderExportAlmacen } : {}),
    ...(defaultProductStockSubAlmacen
      ? { defaultProductStockSubAlmacen }
      : {}),
    orderExportCurrencyMapping,
    ...(orderExportDefaultMoneda ? { orderExportDefaultMoneda } : {}),
    ...(orderExportDefaultTasa != null ? { orderExportDefaultTasa } : {}),
    ...(orderExportDefaultIva != null ? { orderExportDefaultIva } : {}),
    referenceTables,
    taxMapping,
    ...(syncEmployeeIFromLegacy ? { syncEmployeeI: syncEmployeeIFromLegacy } : {}),
    lastSyncAt: lastSyncAtDate,
    syncEntities,
  };
};

const stripUndefined = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as Partial<T>;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;

/**
 * Firestore rejects undefined at any depth.
 * Deep-sanitize payloads before setDoc/updateDoc.
 */
const stripUndefinedDeep = <T>(value: T): T => {
  if (value === undefined) return undefined as T;
  if (Array.isArray(value)) {
    return value
      .map(item => stripUndefinedDeep(item))
      .filter(item => item !== undefined) as T;
  }
  if (!isPlainObject(value)) return value;

  const out: Record<string, unknown> = {};
  for (const [key, nestedValue] of Object.entries(value)) {
    const sanitized = stripUndefinedDeep(nestedValue);
    if (sanitized !== undefined) out[key] = sanitized;
  }
  return out as T;
};

/** Bridge ignores queue items older than this (must match legacy-bridge). */
export const PROFIT_SYNC_QUEUE_TTL_MS = 10 * 60 * 1000;

/**
 * Merge settings from factors/settings with canonical integrations/profitPlus2k8 (integration wins).
 */
function mergeProfitPlus2k8FromIntegrationData(
  factorsPpRaw: unknown,
  integrationData: Record<string, unknown> | null | undefined
): ProfitPlus2k8OrgConfig {
  const base = normalizeProfitPlus2k8Config(factorsPpRaw);
  if (!integrationData) return base;
  return normalizeProfitPlus2k8Config({
    ...base,
    ...integrationData,
  } as Record<string, unknown>);
}

function mergeProfitPlus2k8ForFactors(
  factorsPpRaw: unknown,
  integrationSnap: DocumentSnapshot
): ProfitPlus2k8OrgConfig {
  return mergeProfitPlus2k8FromIntegrationData(
    factorsPpRaw,
    integrationSnap.exists()
      ? (integrationSnap.data() as Record<string, unknown>)
      : null
  );
}

/**
 * Profit Plus 2k8 dedicated integration config (full ideal):
 * organizations/{orgId}/integrations/profitPlus2k8
 */
export const loadProfitPlus2k8IntegrationConfig = async (
  organizationId: string
): Promise<ProfitPlus2k8OrgConfig> => {
  try {
    if (isSelfHosted) {
      const data = await fetchSelfHostedProfitPlusIntegration(organizationId);
      if (!data) return { ...DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG };
      return normalizeProfitPlus2k8Config(data);
    }

    const ref = doc(firestore, 'organizations', organizationId, 'integrations', 'profitPlus2k8');
    const snap = await getDoc(ref);
    if (!snap.exists()) return { ...DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG };
    return normalizeProfitPlus2k8Config(snap.data());
  } catch (e) {
    console.warn('Could not load Profit Plus 2k8 integration config', e);
    return { ...DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG };
  }
};

export const updateProfitPlus2k8IntegrationConfig = async (
  organizationId: string,
  profitPlus2k8: ProfitPlus2k8OrgConfig
): Promise<void> => {
  const sanitized = stripUndefined(profitPlus2k8 as unknown as Record<string, unknown>);
  if (isSelfHosted) {
    await saveSelfHostedProfitPlusIntegration(organizationId, sanitized);
    return;
  }
  const ref = doc(firestore, 'organizations', organizationId, 'integrations', 'profitPlus2k8');
  await setDoc(ref, sanitized, { merge: true });
};

/** True when Profit Plus is enabled and sync type is not "none" (banner + bridge UI apply). */
export function isProfitPlus2k8SyncActivated(
  pp: Partial<ProfitPlus2k8OrgConfig> | undefined | null
): boolean {
  if (!pp?.enabled) return false;
  return pp.syncType !== 'none' && pp.syncType != null;
}

/** True unless explicitly disabled: MayorX owns product.availability by default. */
export function isProfitPlus2k8StockManagedInMayorX(
  pp: Partial<ProfitPlus2k8OrgConfig> | undefined | null
): boolean {
  return pp?.stockManagedInMayorX !== false;
}

/** True unless explicitly disabled: product saves may enqueue MayorX→Profit export. */
export function isProfitPlus2k8SaveProductChangesToProfit(
  pp: Partial<ProfitPlus2k8OrgConfig> | undefined | null
): boolean {
  return pp?.saveProductChangesToProfit !== false;
}

/**
 * Product form Profit tab and inventory Profit helpers: integration on, paso 1 complete,
 * and products included in sync entities (when the list is non-empty).
 */
export function isProfitPlus2k8ProductFormUiEnabled(
  pp: Partial<ProfitPlus2k8OrgConfig> | undefined | null
): boolean {
  if (!pp?.enabled) return false;
  if (!isProfitPlus2k8ReadyForAppFeatures(pp)) return false;
  const entities = pp.syncEntities;
  if (Array.isArray(entities) && entities.length > 0 && !entities.includes('products')) {
    return false;
  }
  return true;
}

/** True unless explicitly disabled: client saves may enqueue export to Profit. */
export function isProfitPlus2k8SaveClientChangesToProfit(
  pp: Partial<ProfitPlus2k8OrgConfig> | undefined | null
): boolean {
  return pp?.saveClientChangesToProfit !== false;
}

/** True unless explicitly disabled: salesperson saves may enqueue export to Profit. */
export function isProfitPlus2k8SaveSalesPersonChangesToProfit(
  pp: Partial<ProfitPlus2k8OrgConfig> | undefined | null
): boolean {
  return pp?.saveSalesPersonChangesToProfit !== false;
}

/**
 * True when the legacy bridge may process `refresh_product` queue items: integration on
 * and sync type is not "none". (Pull-from-Profit does not require the order-export wizard.)
 */
export function shouldQueueProfitProductInventoryRefreshFromProfit(
  pp: Partial<ProfitPlus2k8OrgConfig> | null | undefined
): boolean {
  if (!pp?.enabled) return false;
  if (isProfitPlus2k8StockManagedInMayorX(pp)) return false;
  return isProfitPlus2k8SyncActivated(pp);
}

/**
 * Live updates when integration toggles or sync type changes (keeps banner in sync without reload).
 */
export function subscribeToProfitPlus2k8IntegrationConfig(
  organizationId: string,
  onConfig: (config: ProfitPlus2k8OrgConfig) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  if (isSelfHosted) {
    void loadProfitPlus2k8IntegrationConfig(organizationId)
      .then(onConfig)
      .catch(err => {
        onError?.(err instanceof Error ? err : new Error(String(err)));
        console.error('Error loading Profit Plus 2k8 integration config:', err);
        onConfig({ ...DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG });
      });
    return () => undefined;
  }

  const ref = doc(firestore, 'organizations', organizationId, 'integrations', 'profitPlus2k8');
  return onSnapshot(
    ref,
    (snap) => {
      const raw = snap.exists() ? snap.data() : undefined;
      onConfig(normalizeProfitPlus2k8Config(raw));
    },
    (err) => {
      onError?.(err);
      console.error('Error subscribing to Profit Plus 2k8 integration config:', err);
      onConfig({ ...DEFAULT_PROFIT_PLUS_2K8_ORG_CONFIG });
    }
  );
}

/**
 * Profit Plus 2k8 reference tables stored in subcollections (can be large):
 * organizations/{orgId}/integrations/profitPlus2k8/referenceTables/{tableKey}/rows
 */
export const loadProfitPlus2k8ReferenceTable = async (
  organizationId: string,
  tableKey: keyof ProfitPlus2k8ReferenceTables
): Promise<Array<Record<string, unknown>>> => {
  if (isSelfHosted) {
    return [];
  }

  const rowsRef = collection(
    firestore,
    'organizations',
    organizationId,
    'integrations',
    'profitPlus2k8',
    'referenceTables',
    String(tableKey),
    'rows'
  );
  const snap = await getDocs(rowsRef);
  return snap.docs.map(d => d.data() as Record<string, unknown>);
};

export const loadProfitPlus2k8ReferenceTables = async (
  organizationId: string,
  keys: Array<keyof ProfitPlus2k8ReferenceTables>
): Promise<ProfitPlus2k8ReferenceTables> => {
  const out: ProfitPlus2k8ReferenceTables = {};
  for (const key of keys) {
    const rows = await loadProfitPlus2k8ReferenceTable(organizationId, key);
    (out as any)[key] = rows;
  }
  return out;
};

const sanitizeTaxes = (
  taxes: unknown,
  ivaValue: number
): Array<{ id: string; name: string; rate: number; enabled: boolean }> => {
  const sourceTaxes =
    Array.isArray(taxes) && taxes.length > 0
      ? taxes
      : DEFAULT_TAXES.map(tax => ({
          ...tax,
          enabled: tax.name === 'IVA General',
          rate: tax.name === 'IVA General' ? ivaValue : tax.rate,
        }));

  const usedIds = new Set<string>();
  const sanitized = sourceTaxes
    .map((rawTax, index) => {
      const tax = rawTax as Record<string, unknown>;
      const name =
        typeof tax.name === 'string' && tax.name.trim().length > 0
          ? tax.name.trim()
          : `Impuesto ${index + 1}`;
      const rate =
        typeof tax.rate === 'number' && Number.isFinite(tax.rate)
          ? tax.rate
          : 0;
      const preferredId =
        typeof tax.id === 'string' && tax.id.trim().length > 0
          ? slugifyTaxId(tax.id.trim())
          : slugifyTaxId(name);
      const id = getNextUniqueTaxId(preferredId, usedIds);
      usedIds.add(id);
      return {
        id,
        name,
        rate,
        enabled: Boolean(tax.enabled),
      };
    })
    .filter(tax => tax.name.length > 0);

  return sanitized.length > 0 ? sanitized : DEFAULT_TAXES;
};

const resolvePrimaryTaxId = (
  taxes: Array<{ id: string; name: string; rate: number; enabled: boolean }>,
  currentPrimaryTaxId: unknown
): string => {
  const normalizedCurrent =
    typeof currentPrimaryTaxId === 'string' && currentPrimaryTaxId.trim().length > 0
      ? slugifyTaxId(currentPrimaryTaxId.trim())
      : '';

  if (normalizedCurrent && taxes.some(tax => tax.id === normalizedCurrent)) {
    return normalizedCurrent;
  }

  const ivaGeneral = taxes.find(tax => tax.id === 'iva-general');
  if (ivaGeneral) {
    return ivaGeneral.id;
  }

  return taxes.length > 0 ? taxes[0].id : '';
};

function settingsDataToConfigurationFactors(
  data: Record<string, unknown>,
  integrationData: Record<string, unknown> | null | undefined
): ConfigurationFactors {
  const ivaValue =
    typeof data.iva === 'number' ? data.iva : DEFAULT_CONFIGURATION.iva;
  const taxes = sanitizeTaxes(data.taxes, ivaValue);
  const primaryTaxId = resolvePrimaryTaxId(taxes, data.primaryTaxId);

  return {
    iva: ivaValue,
    availabilityVisible:
      (data.availabilityVisible as boolean | undefined) ??
      (data.disponibilidadVisible as boolean | undefined) ??
      true,
    taxes,
    primaryTaxId,
    paymentMethodsAvailable: (data.paymentMethodsAvailable as ConfigurationFactors['paymentMethodsAvailable']) || [
      { id: 1, name: 'Efectivo', enabled: true },
      { id: 2, name: 'Crédito', enabled: true },
      { id: 3, name: 'Transferencia bancaria', enabled: true },
      { id: 4, name: 'Cheque', enabled: true },
    ],
    orderApprovalRequired: (data.orderApprovalRequired as boolean | undefined) ?? false,
    allowAdminCurrencyModification:
      (data.allowAdminCurrencyModification as boolean | undefined) ?? true,
    allowNegativeInventory: (data.allowNegativeInventory as boolean | undefined) ?? false,
    blockSalesWhenInsufficientStock: (() => {
      const allowNegative =
        (data.allowNegativeInventory as boolean | undefined) ?? false;
      if (allowNegative) return false;
      return (data.blockSalesWhenInsufficientStock as boolean | undefined) ?? false;
    })(),
    inventoryMode: resolveInventoryMode(data.inventoryMode as string | undefined),
    defaultWarehouseId: (data.defaultWarehouseId as string | undefined) || undefined,
    currency: (data.currency as string | undefined) ?? 'VES',
    useReferenceCurrency: (data.useReferenceCurrency as boolean | undefined) ?? true,
    referenceCurrency: (data.referenceCurrency as string | undefined) ?? 'USD',
    exchangeRate: (data.exchangeRate as number | undefined) ?? 36.0,
    useAutomaticExchangeRate:
      (data.useAutomaticExchangeRate as boolean | undefined) ?? false,
    exchangeSourceId: (data.exchangeSourceId as string | undefined) ?? '',
    deliveryNoteLayout:
      data.deliveryNoteLayout &&
      typeof data.deliveryNoteLayout === 'object' &&
      Array.isArray((data.deliveryNoteLayout as { blockOrder?: unknown }).blockOrder)
        ? ({
            blockOrder: (data.deliveryNoteLayout as { blockOrder: unknown[] }).blockOrder,
            blockAlignments:
              (data.deliveryNoteLayout as { blockAlignments?: Record<string, string> })
                .blockAlignments ?? {},
            defaultShowAmounts: (data.deliveryNoteLayout as { defaultShowAmounts?: boolean })
              .defaultShowAmounts,
            defaultAmountCurrencyMode: (
              data.deliveryNoteLayout as { defaultAmountCurrencyMode?: string }
            ).defaultAmountCurrencyMode,
            defaultShowLineDiscountColumn: (
              data.deliveryNoteLayout as { defaultShowLineDiscountColumn?: boolean }
            ).defaultShowLineDiscountColumn,
            typography: (data.deliveryNoteLayout as { typography?: string }).typography,
            blockFontSizes: (data.deliveryNoteLayout as { blockFontSizes?: Record<string, number> })
              .blockFontSizes,
            blockColors: (data.deliveryNoteLayout as { blockColors?: Record<string, string> })
              .blockColors,
            titleIncludesNumber: (data.deliveryNoteLayout as { titleIncludesNumber?: boolean })
              .titleIncludesNumber,
          } as ConfigurationFactors['deliveryNoteLayout'])
        : DEFAULT_DELIVERY_NOTE_LAYOUT,
    profitPlus2k8: mergeProfitPlus2k8FromIntegrationData(data.profitPlus2k8, integrationData),
  };
}

async function loadConfigurationFactorsSelfHosted(
  organizationId: string
): Promise<ConfigurationFactors> {
  const [settingsResult, integrationResult] = await Promise.allSettled([
    fetchSelfHostedFactor(organizationId, 'settings'),
    fetchSelfHostedProfitPlusIntegration(organizationId),
  ]);

  if (settingsResult.status === 'rejected') {
    throw settingsResult.reason;
  }

  const settingsData = settingsResult.value;
  const integrationData =
    integrationResult.status === 'fulfilled' ? integrationResult.value : null;

  if (!settingsData) {
    return {
      ...DEFAULT_CONFIGURATION,
      profitPlus2k8: mergeProfitPlus2k8FromIntegrationData(
        DEFAULT_CONFIGURATION.profitPlus2k8,
        integrationData
      ),
    };
  }

  const hasConfiguredTaxes =
    Array.isArray(settingsData.taxes) && settingsData.taxes.length > 0;
  const ivaValue =
    typeof settingsData.iva === 'number' ? settingsData.iva : DEFAULT_CONFIGURATION.iva;
  const taxes = sanitizeTaxes(settingsData.taxes, ivaValue);
  const primaryTaxId = resolvePrimaryTaxId(taxes, settingsData.primaryTaxId);

  const inventoryDefaultsToBackfill: Record<string, unknown> = {};
  if (settingsData.allowNegativeInventory === undefined) {
    inventoryDefaultsToBackfill.allowNegativeInventory = false;
  }
  if (settingsData.blockSalesWhenInsufficientStock === undefined) {
    inventoryDefaultsToBackfill.blockSalesWhenInsufficientStock = false;
  }

  const shouldBackfillTaxes =
    !hasConfiguredTaxes || settingsData.primaryTaxId !== primaryTaxId;
  if (shouldBackfillTaxes || Object.keys(inventoryDefaultsToBackfill).length) {
    try {
      await saveSelfHostedFactor(organizationId, 'settings', {
        ...(shouldBackfillTaxes ? { taxes, primaryTaxId } : {}),
        ...inventoryDefaultsToBackfill,
      });
    } catch (e) {
      console.warn('Could not backfill default settings for organization', e);
    }
  }

  return settingsDataToConfigurationFactors(settingsData, integrationData);
}

/**
 * Load configuration factors from Firestore
 */
export const loadConfigurationFactors = async (
  organizationId: string
): Promise<ConfigurationFactors> => {
  try {
    if (isSelfHosted) {
      return await loadConfigurationFactorsSelfHosted(organizationId);
    }

    const docRef = doc(
      firestore,
      'organizations',
      organizationId,
      'factors',
      'settings'
    );
    const integrationRef = doc(
      firestore,
      'organizations',
      organizationId,
      'integrations',
      'profitPlus2k8'
    );
    const [docSnap, integrationSnap] = await Promise.all([
      getDoc(docRef),
      getDoc(integrationRef),
    ]);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const ivaValue =
        typeof data.iva === 'number' ? data.iva : DEFAULT_CONFIGURATION.iva;
      const hasConfiguredTaxes =
        Array.isArray(data.taxes) && data.taxes.length > 0;
      const taxes = sanitizeTaxes(data.taxes, ivaValue);
      const primaryTaxId = resolvePrimaryTaxId(taxes, data.primaryTaxId);

      // Backfill defaults for existing orgs missing newer settings fields.
      const inventoryDefaultsToBackfill: Record<string, unknown> = {};
      if (data.allowNegativeInventory === undefined) {
        inventoryDefaultsToBackfill.allowNegativeInventory = false;
      }
      if (data.blockSalesWhenInsufficientStock === undefined) {
        inventoryDefaultsToBackfill.blockSalesWhenInsufficientStock = false;
      }
      const shouldBackfillTaxes =
        !hasConfiguredTaxes || data.primaryTaxId !== primaryTaxId;
      if (shouldBackfillTaxes || Object.keys(inventoryDefaultsToBackfill).length) {
        try {
          await setDoc(
            docRef,
            {
              ...(shouldBackfillTaxes ? { taxes, primaryTaxId } : {}),
              ...inventoryDefaultsToBackfill,
            },
            { merge: true }
          );
        } catch (e) {
          console.warn('Could not backfill default settings for organization', e);
        }
      }

      const allowNegativeInventory = data.allowNegativeInventory ?? false;
      const blockSalesWhenInsufficientStock = allowNegativeInventory
        ? false
        : (data.blockSalesWhenInsufficientStock ?? false);

      const base: ConfigurationFactors = {
        iva: ivaValue,
        availabilityVisible:
          data.availabilityVisible ?? data.disponibilidadVisible ?? true, // Support both old and new field names
        taxes,
        primaryTaxId,
        paymentMethodsAvailable: data.paymentMethodsAvailable || [
          { id: 1, name: 'Efectivo', enabled: true },
          { id: 2, name: 'Crédito', enabled: true },
          { id: 3, name: 'Transferencia bancaria', enabled: true },
          { id: 4, name: 'Cheque', enabled: true },
        ],
        orderApprovalRequired: data.orderApprovalRequired ?? false,
        allowAdminCurrencyModification:
          data.allowAdminCurrencyModification ?? true,
        allowNegativeInventory,
        blockSalesWhenInsufficientStock,
        inventoryMode: resolveInventoryMode(data.inventoryMode as string | undefined),
        defaultWarehouseId: (data.defaultWarehouseId as string | undefined) || undefined,
        // Currency settings moved from organization
        currency: data.currency ?? 'VES',
        useReferenceCurrency: data.useReferenceCurrency ?? true,
        referenceCurrency: data.referenceCurrency ?? 'USD',
        exchangeRate: data.exchangeRate ?? 36.0,
        useAutomaticExchangeRate: data.useAutomaticExchangeRate ?? false,
        exchangeSourceId: data.exchangeSourceId ?? '',
        deliveryNoteLayout:
          data.deliveryNoteLayout && Array.isArray(data.deliveryNoteLayout.blockOrder)
            ? {
                blockOrder: data.deliveryNoteLayout.blockOrder,
                blockAlignments: data.deliveryNoteLayout.blockAlignments ?? {},
                defaultShowAmounts: data.deliveryNoteLayout.defaultShowAmounts,
                defaultAmountCurrencyMode: data.deliveryNoteLayout.defaultAmountCurrencyMode,
                defaultShowLineDiscountColumn:
                  data.deliveryNoteLayout.defaultShowLineDiscountColumn,
                typography: data.deliveryNoteLayout.typography,
                blockFontSizes: data.deliveryNoteLayout.blockFontSizes,
                blockColors: data.deliveryNoteLayout.blockColors,
                titleIncludesNumber: data.deliveryNoteLayout.titleIncludesNumber,
              }
            : DEFAULT_DELIVERY_NOTE_LAYOUT,
        profitPlus2k8: mergeProfitPlus2k8ForFactors(data.profitPlus2k8, integrationSnap),
      };

      // If automatic exchange rate is enabled, attempt to load the selected source's current rate.
      if (base.useAutomaticExchangeRate && base.exchangeSourceId) {
        try {
          const srcRef = doc(
            firestore,
            'exchangeSources',
            base.exchangeSourceId
          );
          const srcSnap = await getDoc(srcRef);
          if (srcSnap.exists()) {
            const src = srcSnap.data() as Record<string, any>;
            if (typeof src.rate === 'number') {
              return { ...base, exchangeRate: src.rate };
            }
          }
        } catch (e) {
          // Ignore source lookup errors and fall back to stored exchangeRate.
          console.warn('Could not load exchange rate from selected source', e);
        }
      }

      return base;
    } else {
      // Auto-create with defaults if configuration doesn't exist yet
      const docRef = doc(
        firestore,
        'organizations',
        organizationId,
        'factors',
        'settings'
      );
      try {
        await setDoc(docRef, DEFAULT_CONFIGURATION);
      } catch (e) {
        // Ignore write error here and still return defaults
        console.warn(
          'Could not create default configuration, using in-memory defaults'
        );
      }
      return {
        ...DEFAULT_CONFIGURATION,
        profitPlus2k8: mergeProfitPlus2k8ForFactors(
          DEFAULT_CONFIGURATION.profitPlus2k8,
          integrationSnap
        ),
      };
    }
  } catch (error) {
    console.error(
      'Error loading configuration factors, returning defaults:',
      error
    );
    return DEFAULT_CONFIGURATION;
  }
};

/**
 * Create initial configuration for an organization
 * This should be called when setting up a new organization
 */
export const createInitialConfiguration = async (
  organizationId: string,
  factors: Partial<ConfigurationFactors> = {}
): Promise<void> => {
  try {
    const docRef = doc(
      firestore,
      'organizations',
      organizationId,
      'factors',
      'settings'
    );

    // Merge provided factors with defaults
    const initialConfig: ConfigurationFactors = {
      ...DEFAULT_CONFIGURATION,
      ...factors,
    };

    await setDoc(docRef, initialConfig);
  } catch (error) {
    console.error('Error creating initial configuration:', error);
    throw error;
  }
};

/**
 * Update configuration factors in Firestore
 */
export const updateConfigurationFactors = async (
  organizationId: string,
  factors: ConfigurationFactors
): Promise<void> => {
  try {
    const payload = stripUndefinedDeep(factors);
    if (isSelfHosted) {
      await saveSelfHostedFactor(
        organizationId,
        'settings',
        payload as unknown as Record<string, unknown>
      );
      return;
    }

    const docRef = doc(
      firestore,
      'organizations',
      organizationId,
      'factors',
      'settings'
    );
    await setDoc(docRef, stripUndefinedDeep(factors));
  } catch (error) {
    console.error('Error updating configuration factors:', error);
    throw error;
  }
};

/**
 * Load notification factors from Firestore
 */
export const loadNotificationFactors = async (
  organizationId: string
): Promise<NotificationFactors> => {
  try {
    if (isSelfHosted) {
      const data = await fetchSelfHostedFactor(organizationId, 'notifications');
      if (!data) return DEFAULT_NOTIFICATION_CONFIG;
      return {
        emailNotifications: (data.emailNotifications as boolean | undefined) ?? true,
        adminEmails: (data.adminEmails as string[] | undefined) ?? [],
        saleCreatedNotification: (data.saleCreatedNotification as boolean | undefined) ?? true,
        saleApprovedNotification: (data.saleApprovedNotification as boolean | undefined) ?? true,
        saleRejectedNotification: (data.saleRejectedNotification as boolean | undefined) ?? true,
        clientOrderCreatedNotification:
          (data.clientOrderCreatedNotification as boolean | undefined) ?? true,
        customerPortalRegisteredNotification:
          (data.customerPortalRegisteredNotification as boolean | undefined) ??
          true,
        returnCreatedNotification: (data.returnCreatedNotification as boolean | undefined) ?? true,
        returnApprovedNotification: (data.returnApprovedNotification as boolean | undefined) ?? true,
        returnRejectedNotification: (data.returnRejectedNotification as boolean | undefined) ?? true,
      };
    }

    const docRef = doc(
      firestore,
      'organizations',
      organizationId,
      'factors',
      'notifications'
    );
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        emailNotifications: data.emailNotifications ?? true,
        adminEmails: data.adminEmails ?? [],
        saleCreatedNotification: data.saleCreatedNotification ?? true,
        saleApprovedNotification: data.saleApprovedNotification ?? true,
        saleRejectedNotification: data.saleRejectedNotification ?? true,
        clientOrderCreatedNotification: data.clientOrderCreatedNotification ?? true,
        customerPortalRegisteredNotification:
          data.customerPortalRegisteredNotification ?? true,
        returnCreatedNotification: data.returnCreatedNotification ?? true,
        returnApprovedNotification: data.returnApprovedNotification ?? true,
        returnRejectedNotification: data.returnRejectedNotification ?? true,
      };
    } else {
      // Auto-create with defaults if configuration doesn't exist yet
      const docRef = doc(
        firestore,
        'organizations',
        organizationId,
        'factors',
        'notifications'
      );
      try {
        await setDoc(docRef, DEFAULT_NOTIFICATION_CONFIG);
      } catch (e) {
        // Ignore write error here and still return defaults
        console.warn(
          'Could not create default notification configuration, using in-memory defaults'
        );
      }
      return DEFAULT_NOTIFICATION_CONFIG;
    }
  } catch (error) {
    console.error(
      'Error loading notification factors, returning defaults:',
      error
    );
    return DEFAULT_NOTIFICATION_CONFIG;
  }
};

/**
 * Update notification factors in Firestore
 */
export const updateNotificationFactors = async (
  organizationId: string,
  factors: NotificationFactors
): Promise<void> => {
  try {
    if (isSelfHosted) {
      await saveSelfHostedFactor(
        organizationId,
        'notifications',
        factors as unknown as Record<string, unknown>
      );
      return;
    }

    const docRef = doc(
      firestore,
      'organizations',
      organizationId,
      'factors',
      'notifications'
    );
    await setDoc(docRef, factors);
  } catch (error) {
    console.error('Error updating notification factors:', error);
    throw error;
  }
};

/**
 * Load branding factors from Firestore
 */
export const loadBrandingFactors = async (
  organizationId: string
): Promise<BrandingFactors> => {
  try {
    if (isSelfHosted) {
      const data = await fetchSelfHostedFactor(organizationId, 'branding');
      if (!data) return DEFAULT_BRANDING_CONFIG;
      return {
        logo: (data.logo as string | undefined) ?? DEFAULT_BRANDING_CONFIG.logo,
        logoStoragePath: data.logoStoragePath as string | undefined,
        primaryColor:
          (data.primaryColor as string | undefined) ?? DEFAULT_BRANDING_CONFIG.primaryColor,
        secondaryColor:
          (data.secondaryColor as string | undefined) ?? DEFAULT_BRANDING_CONFIG.secondaryColor,
        tertiaryColor:
          (data.tertiaryColor as string | undefined) ?? DEFAULT_BRANDING_CONFIG.tertiaryColor,
        font: (data.font as string | undefined) ?? DEFAULT_BRANDING_CONFIG.font,
      };
    }

    const docRef = doc(
      firestore,
      'organizations',
      organizationId,
      'factors',
      'branding'
    );
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        logo: data.logo ?? DEFAULT_BRANDING_CONFIG.logo,
        logoStoragePath: data.logoStoragePath,
        primaryColor: data.primaryColor ?? DEFAULT_BRANDING_CONFIG.primaryColor,
        secondaryColor:
          data.secondaryColor ?? DEFAULT_BRANDING_CONFIG.secondaryColor,
        tertiaryColor:
          data.tertiaryColor ?? DEFAULT_BRANDING_CONFIG.tertiaryColor,
        font: data.font ?? DEFAULT_BRANDING_CONFIG.font,
      };
    } else {
      // Auto-create with defaults if configuration doesn't exist yet
      const docRef = doc(
        firestore,
        'organizations',
        organizationId,
        'factors',
        'branding'
      );
      try {
        await setDoc(docRef, DEFAULT_BRANDING_CONFIG);
      } catch (e) {
        console.warn(
          'Could not create default branding configuration, using in-memory defaults'
        );
      }
      return DEFAULT_BRANDING_CONFIG;
    }
  } catch (error) {
    console.error('Error loading branding factors, returning defaults:', error);
    return DEFAULT_BRANDING_CONFIG;
  }
};

/**
 * Update branding factors in Firestore
 */
export const updateBrandingFactors = async (
  organizationId: string,
  factors: BrandingFactors
): Promise<void> => {
  try {
    const sanitizedFactors = Object.entries(factors).reduce(
      (acc, [key, value]) => {
        if (value !== undefined) {
          acc[key as keyof BrandingFactors] =
            value as BrandingFactors[keyof BrandingFactors];
        }
        return acc;
      },
      {} as Partial<BrandingFactors>
    );

    if (isSelfHosted) {
      await saveSelfHostedFactor(
        organizationId,
        'branding',
        sanitizedFactors as Record<string, unknown>
      );
      return;
    }

    // Firestore rejects fields explicitly set to undefined, so strip them out
    const docRef = doc(
      firestore,
      'organizations',
      organizationId,
      'factors',
      'branding'
    );
    await setDoc(docRef, sanitizedFactors);
  } catch (error) {
    console.error('Error updating branding factors:', error);
    throw error;
  }
};

/**
 * Clear branding logo and remove storage path atomically in Firestore
 */
export const clearBrandingLogo = async (
  organizationId: string
): Promise<void> => {
  try {
    const docRef = doc(
      firestore,
      'organizations',
      organizationId,
      'factors',
      'branding'
    );
    await setDoc(
      docRef,
      {
        logo: '',
        logoStoragePath: deleteField(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error clearing branding logo:', error);
    throw error;
  }
};

/** Available entity counts in Profit Plus (written by bridge). */
export interface ProfitPlus2k8AvailableCounts {
  salesPeople?: number;
  clients?: number;
  products?: number;
  sales?: number;
}

/** Bridge status written by the legacy desktop app (MayorX Bridge). */
export interface ProfitPlus2k8BridgeStatus {
  connected: boolean;
  lastSeenAt: Date | null;
  user?: string;
  /** Counts in ERP for initial setup wizard. */
  availableCounts?: ProfitPlus2k8AvailableCounts;
  /** Set by admin when user completes the setup wizard. */
  initialSetupCompleted?: boolean;
  /** @deprecated Legacy: wizard used requestedSync on the integration doc; use syncQueue (kind=import) instead. */
  requestedSync?: string;
  /** Bridge wrote reference tables (stored under integrations/<orgId>/referenceTables in subcollections). */
  referenceDataReady?: boolean;
  /** Lightweight metadata about the reference tables that are available. */
  referenceDataMeta?: {
    tables?: Record<string, number>;
  };
  /**
   * Backward compatibility: older bridge versions wrote full reference tables inside this field.
   * New ideal flow loads them from `integrations/profitPlus2k8/referenceTables/<tableKey>/...`.
   */
  referenceData?: ProfitPlus2k8ReferenceTables;
}

const BRIDGE_STATUS_STALE_MS = 5 * 60 * 1000; // 5 minutes — bridge considered off if lastSeenAt older

function parseBridgeStatusDoc(data: Record<string, unknown> | undefined): ProfitPlus2k8BridgeStatus {
  if (!data) {
    return { connected: false, lastSeenAt: null };
  }
  const connected = Boolean(data.connected);
  let lastSeenAt: Date | null = null;
  if (data.lastSeenAt) {
    const t = data.lastSeenAt as { toDate?: () => Date };
    lastSeenAt = typeof t.toDate === 'function' ? t.toDate() : new Date(data.lastSeenAt as number);
  }
  const now = Date.now();
  const isStale = lastSeenAt ? now - lastSeenAt.getTime() > BRIDGE_STATUS_STALE_MS : true;
  const rawCounts = data.availableCounts;
  const availableCounts: ProfitPlus2k8AvailableCounts | undefined =
    rawCounts && typeof rawCounts === 'object' && !Array.isArray(rawCounts)
      ? {
          salesPeople: Number((rawCounts as Record<string, unknown>).salesPeople) || 0,
          clients: Number((rawCounts as Record<string, unknown>).clients) || 0,
          products: Number((rawCounts as Record<string, unknown>).products) || 0,
          sales: Number((rawCounts as Record<string, unknown>).sales) || 0,
        }
      : undefined;
  const rawRef = data.referenceData;
  const referenceData: ProfitPlus2k8ReferenceTables | undefined =
    rawRef && typeof rawRef === 'object' && !Array.isArray(rawRef) ? (rawRef as ProfitPlus2k8ReferenceTables) : undefined;

  const referenceDataReady = Boolean(data.referenceDataReady);
  const referenceDataMeta: { tables?: Record<string, number> } | undefined = (() => {
    const meta = data.referenceDataMeta;
    if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return undefined;
    const tables = (meta as Record<string, unknown>).tables;
    if (!tables || typeof tables !== 'object' || Array.isArray(tables)) return undefined;
    const normalized: Record<string, number> = {};
    for (const [k, v] of Object.entries(tables as Record<string, unknown>)) {
      const n = Number(v);
      if (Number.isFinite(n)) normalized[k] = n;
    }
    return { tables: normalized };
  })();
  const initialSetupCompleted = resolveProfitPlus2k8InitialSetupCompleted(data);

  return {
    connected: connected && !isStale,
    lastSeenAt,
    user: typeof data.user === 'string' ? data.user : undefined,
    availableCounts,
    initialSetupCompleted,
    requestedSync: typeof data.requestedSync === 'string' ? data.requestedSync : undefined,
    referenceDataReady,
    referenceDataMeta,
    referenceData,
  };
}

/**
 * Subscribe to Profit Plus 2k8 bridge status for an organization.
 * Bridge heartbeat and sync flags live on the same document as integration config:
 * organizations/{orgId}/integrations/profitPlus2k8 (merge writes).
 * Consider "connected" only when connected === true and lastSeenAt is within 5 minutes.
 */
export function subscribeToProfitPlus2k8BridgeStatus(
  organizationId: string,
  onStatus: (status: ProfitPlus2k8BridgeStatus) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  if (isSelfHosted) {
    void fetchSelfHostedProfitPlusIntegration(organizationId)
      .then(data => onStatus(parseBridgeStatusDoc(data ?? undefined)))
      .catch(err => {
        onError?.(err instanceof Error ? err : new Error(String(err)));
        console.error('Error loading Profit Plus 2k8 bridge status:', err);
        onStatus({ connected: false, lastSeenAt: null });
      });
    return () => undefined;
  }

  const ref = doc(
    firestore,
    'organizations',
    organizationId,
    'integrations',
    'profitPlus2k8'
  );
  return onSnapshot(
    ref,
    (snap) => {
      const data = snap.exists() ? (snap.data() as Record<string, unknown>) : undefined;
      onStatus(parseBridgeStatusDoc(data));
    },
    (err) => {
      onError?.(err);
      console.error('Error subscribing to Profit Plus 2k8 bridge status:', err);
      onStatus({ connected: false, lastSeenAt: null });
    }
  );
}

/** Same document as integration config; bridge uses merge for runtime fields. */
const BRIDGE_STATUS_REF = (organizationId: string) =>
  doc(firestore, 'organizations', organizationId, 'integrations', 'profitPlus2k8');

export type ProfitPlus2k8ImportEntity =
  | 'referenceData'
  | 'salesPeople'
  | 'clients'
  | 'products'
  | 'sales';

/**
 * Enqueue a Profit → MayorX import (setup wizard). Same FIFO queue as exports; bridge deletes the doc when done.
 */
export async function enqueueProfitImportSyncRequest(
  organizationId: string,
  importEntity: ProfitPlus2k8ImportEntity
): Promise<DocumentReference> {
  const q = collection(
    firestore,
    'organizations',
    organizationId,
    'integrations',
    'profitPlus2k8',
    'syncQueue'
  );
  return addDoc(q, {
    kind: 'import',
    importEntity,
    createdAt: serverTimestamp(),
  });
}

/**
 * Request the bridge to run a wizard import (referenceData, salesPeople, …). Uses syncQueue only.
 * Returns the queue document ref (e.g. for onSnapshot until the bridge removes it).
 */
export async function requestProfitPlus2k8Sync(
  organizationId: string,
  entity: ProfitPlus2k8ImportEntity
): Promise<DocumentReference> {
  return enqueueProfitImportSyncRequest(organizationId, entity);
}

export type ProfitSyncQueueKind =
  | 'order'
  | 'client'
  | 'product'
  | 'salesPerson'
  | 'refresh_product'
  | 'refresh_client';

/**
 * FIFO export queue (avoids last-write-wins on concurrent users). Items expire after {@link PROFIT_SYNC_QUEUE_TTL_MS}.
 * Path: organizations/{orgId}/integrations/profitPlus2k8/syncQueue/{autoId}
 * Kinds: order | client | product (MayorX → Profit), refresh_product (pull dbo.art + st_almac for one product id),
 * refresh_client (pull dbo.clientes + JOINs for one client id).
 */
export async function enqueueProfitSyncExportRequest(
  organizationId: string,
  kind: ProfitSyncQueueKind,
  targetId: string
): Promise<void> {
  const q = collection(
    firestore,
    'organizations',
    organizationId,
    'integrations',
    'profitPlus2k8',
    'syncQueue'
  );
  await addDoc(q, {
    kind,
    targetId: String(targetId).trim(),
    createdAt: serverTimestamp(),
  });
}

/**
 * Request the bridge to sync an approved order to Profit Plus (create pedido).
 */
export async function requestOrderSyncToProfit(
  organizationId: string,
  orderId: string
): Promise<void> {
  await enqueueProfitSyncExportRequest(organizationId, 'order', orderId);
}

/**
 * Request the legacy bridge to export this product to Profit Plus (INSERT dbo.art if new code, else UPDATE).
 */
export async function requestProductSyncToProfit(
  organizationId: string,
  productId: string
): Promise<void> {
  await enqueueProfitSyncExportRequest(organizationId, 'product', productId);
}

/**
 * Queue a pull from Profit Plus for one product: refresh availability and profitPlus2k8 stock fields from dbo.art / st_almac.
 * Processed by the legacy bridge (same FIFO syncQueue as exports).
 */
export async function requestProductInventoryRefreshFromProfit(
  organizationId: string,
  productId: string
): Promise<void> {
  await enqueueProfitSyncExportRequest(
    organizationId,
    'refresh_product',
    productId
  );
}

/**
 * Queue pull-from-Profit refresh for many distinct product ids (same FIFO queue as single refresh).
 */
export async function requestProductInventoryRefreshFromProfitForProductIds(
  organizationId: string,
  productIds: readonly string[]
): Promise<void> {
  const unique = Array.from(
    new Set(productIds.map(id => String(id).trim()).filter(Boolean))
  );
  if (!unique.length) return;
  await Promise.allSettled(
    unique.map(pid =>
      requestProductInventoryRefreshFromProfit(organizationId, pid)
    )
  );
}

/**
 * Queue a pull from Profit Plus for one client: refresh app fields and profitPlus2k8.fields from dbo.clientes (+ JOINs).
 * Processed by the legacy bridge (same FIFO syncQueue as exports).
 */
export async function requestClientRefreshFromProfit(
  organizationId: string,
  clientId: string
): Promise<void> {
  await enqueueProfitSyncExportRequest(
    organizationId,
    'refresh_client',
    clientId
  );
}

/**
 * Request the legacy bridge to export this client to Profit Plus (INSERT dbo.clientes if new code, else UPDATE).
 */
export async function requestClientSyncToProfit(
  organizationId: string,
  clientId: string
): Promise<void> {
  await enqueueProfitSyncExportRequest(organizationId, 'client', clientId);
}

/**
 * Request the legacy bridge to export this sales person to Profit Plus (INSERT dbo.vendedor if new code, else UPDATE).
 */
export async function requestSalesPersonSyncToProfit(
  organizationId: string,
  salesPersonId: string
): Promise<void> {
  await enqueueProfitSyncExportRequest(
    organizationId,
    'salesPerson',
    salesPersonId
  );
}

/**
 * Mark initial reference export config (wizard step 1) as saved on the integration doc.
 * Call this only after «Guardar en configuración» on step 1 (almacén, moneda, tasa).
 */
export async function setProfitPlus2k8InitialSetupCompleted(
  organizationId: string
): Promise<void> {
  const ref = BRIDGE_STATUS_REF(organizationId);
  await setDoc(ref, { initialSetupCompleted: true }, { merge: true });
}

/**
 * Reset initial setup so the setup wizard can be run again (e.g. not satisfied with imports).
 */
export async function resetProfitPlus2k8InitialSetup(
  organizationId: string
): Promise<void> {
  const ref = BRIDGE_STATUS_REF(organizationId);
  await setDoc(ref, { initialSetupCompleted: false }, { merge: true });
}

/** Staging item from Profit Plus (bridge writes to staging; admin reads and user chooses what to import). */
export interface ProfitPlus2k8StagingItem {
  id: string;
  [key: string]: unknown;
}

/** Response from backend list-staging endpoint. */
export interface ProfitPlus2k8StagingResponse {
  entity: string;
  items: ProfitPlus2k8StagingItem[];
  total: number;
  /** Present when more documents exist after this page (pass as pageToken). */
  nextPageToken?: string | null;
}

/** Response from backend promote endpoint. */
export interface ProfitPlus2k8PromoteResponse {
  promoted: number;
  errors: number;
}

/**
 * List Profit Plus staging documents for an entity (data pulled by bridge; user chooses what to import).
 * Uses backend API (authenticated). Responses are paginated; use nextPageToken with pageToken for more rows.
 */
export async function listProfitPlus2k8Staging(
  organizationId: string,
  entity: 'salesPeople' | 'clients' | 'products' | 'sales',
  options?: { limit?: number; pageToken?: string | null }
): Promise<ProfitPlus2k8StagingResponse> {
  const { authenticatedHttpClient } = await import('./httpClient');
  const params: Record<string, string | number> = {};
  if (options?.limit != null) {
    params.limit = options.limit;
  }
  if (options?.pageToken) {
    params.pageToken = options.pageToken;
  }
  return authenticatedHttpClient.get<ProfitPlus2k8StagingResponse>(
    `/profit-plus/${organizationId}/staging/${entity}`,
    Object.keys(params).length ? { params } : undefined
  );
}

/** Timeout for promote request (large batches can take a while). Used only when WebSocket is unavailable. */
const PROMOTE_REQUEST_TIMEOUT_MS = 120000; // 2 minutes

/**
 * Promote selected staging documents to the main org collection (import selected).
 * Prefer promoteProfitPlus2k8StagingViaWebSocket for large batches (no timeout, progress).
 */
export async function promoteProfitPlus2k8Staging(
  organizationId: string,
  entity: 'salesPeople' | 'clients' | 'products' | 'sales',
  documentIds: string[],
  options?: { promoteAll?: boolean }
): Promise<ProfitPlus2k8PromoteResponse> {
  const { authenticatedHttpClient } = await import('./httpClient');
  return authenticatedHttpClient.post<ProfitPlus2k8PromoteResponse>(
    `/profit-plus/${organizationId}/promote`,
    {
      entity,
      documentIds,
      promoteAll: options?.promoteAll === true,
    },
    { timeout: PROMOTE_REQUEST_TIMEOUT_MS }
  );
}

/** Progress payload sent over the promote WebSocket. */
export interface ProfitPlus2k8PromoteProgress {
  processed: number;
  total: number;
  promoted: number;
  errors: number;
}

/**
 * Promote via Socket.IO (same transport as inventory/clients bulk import): no request timeout, progress updates.
 * Connects to the same server, emits promoteStaging after "connected", listens for progress/success/error.
 */
export async function promoteProfitPlus2k8StagingViaWebSocket(
  organizationId: string,
  entity: 'salesPeople' | 'clients' | 'products' | 'sales',
  documentIds: string[],
  onProgress: (progress: ProfitPlus2k8PromoteProgress) => void,
  options?: { promoteAll?: boolean }
): Promise<ProfitPlus2k8PromoteResponse> {
  const baseURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
  const { io } = await import('socket.io-client');

  return new Promise((resolve, reject) => {
    // Polling only: works when uvicorn is run with --ws none (no WebSocket upgrade)
    const socket = io(baseURL, {
      transports: ['polling'],
      timeout: 60000,
      forceNew: true,
    });
    let resolved = false;
    const cleanup = () => {
      socket.off('connected');
      socket.off('progress');
      socket.off('success');
      socket.off('error');
      socket.off('connect_error');
      socket.disconnect();
    };

    socket.on('connect_error', (err) => {
      if (!resolved) {
        resolved = true;
        cleanup();
        reject(err || new Error('Socket connection failed'));
      }
    });

    socket.on('connected', () => {
      socket.emit('promoteStaging', {
        organizationId,
        entity,
        documentIds,
        promoteAll: options?.promoteAll === true,
      });
    });

    socket.on('progress', (data: { processed?: number; total?: number; promoted?: number; errors?: number }) => {
      if (typeof data.processed === 'number' && typeof data.total === 'number') {
        onProgress({
          processed: data.processed,
          total: data.total,
          promoted: data.promoted ?? 0,
          errors: data.errors ?? 0,
        });
      }
    });

    socket.on('success', (data: ProfitPlus2k8PromoteResponse) => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(data);
      }
    });

    socket.on('error', (payload: { message?: string }) => {
      if (!resolved) {
        resolved = true;
        cleanup();
        reject(new Error(payload?.message ?? 'Promote failed'));
      }
    });
  });
}
